import React from 'react';
import PropTypes from 'prop-types';

/**
 * Premium Layout Components
 * Provides responsive spacing, visual hierarchy, and consistent layouts
 */

/**
 * Page - Full page container with proper structure
 */
export const Page = ({ children, className = '' }) => (
  <div className={`min-h-screen bg-gradient-to-br from-surface-dark via-surface to-surface-dark ${className}`}>
    {children}
  </div>
);

/**
 * PageHeader - Consistent page header with title and actions
 * Uses semantic <header> element with proper ARIA labeling
 */
export const PageHeader = ({
  title,
  subtitle,
  icon: Icon,
  actions,
  badge,
  className = '',
  ariaLabel,
}) => (
  <header
    className={`px-4 sm:px-6 lg:px-8 py-4 sm:py-6 glass border-b ${className}`}
    aria-label={ariaLabel || `${title} header`}
  >
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        {Icon && (
          <div className="p-2 sm:p-2.5 rounded-xl bg-primary-500/20">
            <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary-400" />
          </div>
        )}
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-semibold text-white">{title}</h1>
            {badge && (
              <span className="px-2 py-0.5 rounded-full bg-primary-500/20 text-primary-300 text-xs font-medium">
                {badge}
              </span>
            )}
          </div>
          {subtitle && (
            <p className="text-xs sm:text-sm text-gray-400 mt-0.5">{subtitle}</p>
          )}
        </div>
      </div>
      {actions && (
        <div className="flex items-center gap-2 sm:gap-3">
          {actions}
        </div>
      )}
    </div>
  </header>
);

PageHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  icon: PropTypes.elementType,
  actions: PropTypes.node,
  badge: PropTypes.string,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

/**
 * PageContent - Main content area with responsive padding
 * Uses semantic <main> element and provides skip link target
 */
export const PageContent = ({ children, className = '', ariaLabel = 'Main content' }) => (
  <main
    id="main-content"
    className={`p-4 sm:p-6 lg:p-8 ${className}`}
    aria-label={ariaLabel}
    tabIndex={-1}
  >
    {children}
  </main>
);

PageContent.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

/**
 * PageFooter - Footer bar with minimal footprint
 * Uses semantic <footer> element
 */
export const PageFooter = ({ children, className = '', ariaLabel = 'Page footer' }) => (
  <footer
    className={`px-4 sm:px-6 lg:px-8 py-3 sm:py-4 glass border-t ${className}`}
    aria-label={ariaLabel}
  >
    {children}
  </footer>
);

PageFooter.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

/**
 * PageSidebar - Semantic aside element for navigation sidebars
 * Uses <aside> for complementary content that could stand alone
 */
export const PageSidebar = ({
  children,
  className = '',
  ariaLabel = 'Sidebar navigation',
  position = 'left',
}) => (
  <aside
    className={`
      glass
      ${position === 'left' ? 'border-r' : 'border-l'}
      ${className}
    `}
    aria-label={ariaLabel}
  >
    {children}
  </aside>
);

PageSidebar.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
  position: PropTypes.oneOf(['left', 'right']),
};

/**
 * PageNav - Semantic nav element for primary navigation
 * Uses <nav> for site-wide navigation
 */
export const PageNav = ({
  children,
  className = '',
  ariaLabel = 'Main navigation',
}) => (
  <nav
    className={`${className}`}
    aria-label={ariaLabel}
  >
    {children}
  </nav>
);

PageNav.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

/**
 * Section - Vertical section with proper spacing
 */
export const Section = ({
  children,
  title,
  subtitle,
  actions,
  size = 'md',
  className = '',
}) => {
  const spacing = {
    sm: 'space-y-3',
    md: 'space-y-4 sm:space-y-5',
    lg: 'space-y-5 sm:space-y-6',
    xl: 'space-y-6 sm:space-y-8',
  };

  return (
    <section className={`${spacing[size]} ${className}`}>
      {(title || subtitle || actions) && (
        <div className="flex items-start justify-between gap-4">
          <div>
            {title && (
              <h2 className="text-base sm:text-lg font-semibold text-white">{title}</h2>
            )}
            {subtitle && (
              <p className="text-xs sm:text-sm text-gray-400 mt-0.5">{subtitle}</p>
            )}
          </div>
          {actions && (
            <div className="flex items-center gap-2">
              {actions}
            </div>
          )}
        </div>
      )}
      {children}
    </section>
  );
};

Section.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  actions: PropTypes.node,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  className: PropTypes.string,
};

/**
 * Grid - Responsive grid layout
 */
export const Grid = ({
  children,
  cols = 1,
  sm,
  md,
  lg,
  xl,
  gap = 'md',
  className = '',
}) => {
  const colClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
    6: 'grid-cols-6',
  };

  const gapClasses = {
    xs: 'gap-2',
    sm: 'gap-3',
    md: 'gap-4',
    lg: 'gap-5 sm:gap-6',
    xl: 'gap-6 sm:gap-8',
  };

  const responsiveClasses = [
    colClasses[cols],
    sm && `sm:${colClasses[sm]}`,
    md && `md:${colClasses[md]}`,
    lg && `lg:${colClasses[lg]}`,
    xl && `xl:${colClasses[xl]}`,
  ].filter(Boolean).join(' ');

  return (
    <div className={`grid ${responsiveClasses} ${gapClasses[gap]} ${className}`}>
      {children}
    </div>
  );
};

Grid.propTypes = {
  children: PropTypes.node.isRequired,
  cols: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
  sm: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
  md: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
  lg: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
  xl: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
  gap: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  className: PropTypes.string,
};

/**
 * Stack - Vertical flex container with spacing
 */
export const Stack = ({
  children,
  gap = 'md',
  align = 'stretch',
  justify = 'start',
  className = '',
}) => {
  const gapClasses = {
    none: 'gap-0',
    xs: 'gap-1',
    sm: 'gap-2',
    md: 'gap-3 sm:gap-4',
    lg: 'gap-4 sm:gap-6',
    xl: 'gap-6 sm:gap-8',
  };

  const alignClasses = {
    start: 'items-start',
    center: 'items-center',
    end: 'items-end',
    stretch: 'items-stretch',
  };

  const justifyClasses = {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
    between: 'justify-between',
    around: 'justify-around',
  };

  return (
    <div className={`flex flex-col ${gapClasses[gap]} ${alignClasses[align]} ${justifyClasses[justify]} ${className}`}>
      {children}
    </div>
  );
};

Stack.propTypes = {
  children: PropTypes.node.isRequired,
  gap: PropTypes.oneOf(['none', 'xs', 'sm', 'md', 'lg', 'xl']),
  align: PropTypes.oneOf(['start', 'center', 'end', 'stretch']),
  justify: PropTypes.oneOf(['start', 'center', 'end', 'between', 'around']),
  className: PropTypes.string,
};

/**
 * Cluster - Horizontal flex container with wrapping
 */
export const Cluster = ({
  children,
  gap = 'md',
  align = 'center',
  justify = 'start',
  wrap = true,
  className = '',
}) => {
  const gapClasses = {
    none: 'gap-0',
    xs: 'gap-1',
    sm: 'gap-2',
    md: 'gap-3',
    lg: 'gap-4',
    xl: 'gap-6',
  };

  const alignClasses = {
    start: 'items-start',
    center: 'items-center',
    end: 'items-end',
    baseline: 'items-baseline',
  };

  const justifyClasses = {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
    between: 'justify-between',
    around: 'justify-around',
  };

  return (
    <div className={`flex ${wrap ? 'flex-wrap' : ''} ${gapClasses[gap]} ${alignClasses[align]} ${justifyClasses[justify]} ${className}`}>
      {children}
    </div>
  );
};

Cluster.propTypes = {
  children: PropTypes.node.isRequired,
  gap: PropTypes.oneOf(['none', 'xs', 'sm', 'md', 'lg', 'xl']),
  align: PropTypes.oneOf(['start', 'center', 'end', 'baseline']),
  justify: PropTypes.oneOf(['start', 'center', 'end', 'between', 'around']),
  wrap: PropTypes.bool,
  className: PropTypes.string,
};

/**
 * Split - Two-column layout with flexible sizing
 */
export const Split = ({
  children,
  ratio = '1:1',
  gap = 'lg',
  vertical = 'md',
  className = '',
}) => {
  const ratioClasses = {
    '1:1': 'lg:grid-cols-2',
    '1:2': 'lg:grid-cols-3 [&>*:first-child]:lg:col-span-1 [&>*:last-child]:lg:col-span-2',
    '2:1': 'lg:grid-cols-3 [&>*:first-child]:lg:col-span-2 [&>*:last-child]:lg:col-span-1',
    '1:3': 'lg:grid-cols-4 [&>*:first-child]:lg:col-span-1 [&>*:last-child]:lg:col-span-3',
    '3:1': 'lg:grid-cols-4 [&>*:first-child]:lg:col-span-3 [&>*:last-child]:lg:col-span-1',
    '2:3': 'lg:grid-cols-5 [&>*:first-child]:lg:col-span-2 [&>*:last-child]:lg:col-span-3',
    '3:2': 'lg:grid-cols-5 [&>*:first-child]:lg:col-span-3 [&>*:last-child]:lg:col-span-2',
  };

  const gapClasses = {
    sm: 'gap-3 sm:gap-4',
    md: 'gap-4 sm:gap-5 lg:gap-6',
    lg: 'gap-5 sm:gap-6 lg:gap-8',
    xl: 'gap-6 sm:gap-8 lg:gap-10',
  };

  return (
    <div className={`grid grid-cols-1 ${ratioClasses[ratio]} ${gapClasses[gap]} ${className}`}>
      {children}
    </div>
  );
};

Split.propTypes = {
  children: PropTypes.node.isRequired,
  ratio: PropTypes.oneOf(['1:1', '1:2', '2:1', '1:3', '3:1', '2:3', '3:2']),
  gap: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  className: PropTypes.string,
};

/**
 * Empty - Empty state placeholder
 */
export const Empty = ({
  icon: Icon,
  title,
  description,
  action,
  className = '',
}) => (
  <div className={`flex flex-col items-center justify-center py-12 sm:py-16 ${className}`}>
    {Icon && (
      <Icon className="w-12 h-12 sm:w-16 sm:h-16 text-gray-600 mb-4" />
    )}
    {title && (
      <p className="text-gray-400 text-center font-medium">{title}</p>
    )}
    {description && (
      <p className="text-sm text-gray-600 text-center mt-1 max-w-sm">{description}</p>
    )}
    {action && (
      <div className="mt-4">
        {action}
      </div>
    )}
  </div>
);

Empty.propTypes = {
  icon: PropTypes.elementType,
  title: PropTypes.string,
  description: PropTypes.string,
  action: PropTypes.node,
  className: PropTypes.string,
};

export default {
  Page,
  PageHeader,
  PageContent,
  PageFooter,
  Section,
  Grid,
  Stack,
  Cluster,
  Split,
  Empty,
};
